/**
 * Champs d'une carte (design system, Editeur) : Markdown rendu en direct dans la zone, sans
 * panneau d'aperçu (ADR 0008). Bibliothèque : CodeMirror 6, qui permet la fermeture
 * automatique, la règle « Entrée = sortir du format » et un rendu en direct fidèle à la
 * carte en séance (décorations sur l'arbre syntaxique, le même que l'affichage).
 */
import {
  defaultKeymap,
  history,
  historyKeymap,
  indentLess,
  indentMore,
} from '@codemirror/commands';
import { markdownKeymap } from '@codemirror/lang-markdown';
import { syntaxTree } from '@codemirror/language';
import { Compartment, EditorState, type Extension } from '@codemirror/state';
import { EditorView, keymap, placeholder } from '@codemirror/view';
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { MessageDeChamp } from '../composants/MessageDeChamp';
import { apercuEnDirect, type ResoudreImage } from './apercu';
import { BarreDOutilsEditeur } from './BarreDOutilsEditeur';
import {
  appliquerFormat,
  entree,
  fermetureAuto,
  formatsActifs,
  supprimerPaireVide,
  type FormatDEditeur,
} from './commandes';
import { langageCartes } from './langage';
import { preparerImage } from './images';

export type NomChamp = 'recto' | 'verso' | 'texte';

export interface ChampDEditeur {
  nom: NomChamp;
  libelle: string;
  exemple?: string;
  /** Hauteur minimale en lignes de 26 px. */
  lignes?: number;
}

export interface EditeurProps {
  type: 'basique' | 'trous' | 'image' | 'tableau';
  champs: ChampDEditeur[];
  valeur: Partial<Record<NomChamp, string>>;
  onChange: (nom: NomChamp, markdown: string) => void;
  erreurs?: Partial<Record<NomChamp, string>>;
  desactive?: boolean;
  onEnregistrer?: () => void;
  onAnnuler?: () => void;
  /** Envoie une image (déjà réduite) et renvoie son chemin. */
  envoyerImage: (image: Blob, extension: string) => Promise<string>;
  resoudreImage: ResoudreImage;
  couleursPersonnelles: string[];
  onAjouterCouleur: (couleur: string) => void;
  /** Erreur d'envoi d'image, etc. */
  onErreur?: (message: string) => void;
  /** Barre d'outils collée en haut de la zone qui défile. */
  barreCollee?: boolean;
  /** Contenu entre la barre et les champs (rare). */
  avantChamps?: ReactNode;
}

export interface EditeurPoignee {
  /** Donne le focus au premier champ (ou à celui nommé). */
  focus(nom?: NomChamp): void;
}

const CLE_SURLIGNAGE = 'flashcard-surlignage';

function lireSurlignage(): string {
  try {
    return window.localStorage.getItem(CLE_SURLIGNAGE) ?? 'jaune';
  } catch {
    return 'jaune';
  }
}

/** Couleur du surlignage au curseur : `jaune`, une couleur de base, `#…`, ou `null`. */
function surlignageAuCurseur(state: EditorState): string | null {
  const p = state.selection.main.head;
  for (const cote of [-1, 1] as const) {
    for (let n = syntaxTree(state).resolveInner(p, cote); n; n = n.parent!) {
      if (n.name === 'Surlignage') {
        const ouvrante = state.sliceDoc(n.firstChild?.from ?? n.from, n.firstChild?.to ?? n.from);
        return /^==\{(.+)\}$/.exec(ouvrante)?.[1] ?? 'jaune';
      }
      if (!n.parent) break;
    }
  }
  return null;
}

function dansListeOuCode(state: EditorState): boolean {
  for (let n = syntaxTree(state).resolveInner(state.selection.main.head, -1); n; n = n.parent!) {
    if (n.name === 'ListItem' || n.name === 'FencedCode' || n.name === 'CodeBlock') return true;
    if (!n.parent) break;
  }
  return false;
}

interface ChampProps {
  champ: ChampDEditeur;
  type: EditeurProps['type'];
  valeur: string;
  erreur: string | undefined;
  desactive: boolean;
  extensionsCommunes: Extension;
  onChange: (markdown: string) => void;
  onVue: (vue: EditorView | null) => void;
  onEtat: (vue: EditorView) => void;
}

function Champ({
  champ,
  type,
  valeur,
  erreur,
  desactive,
  extensionsCommunes,
  onChange,
  onVue,
  onEtat,
}: ChampProps) {
  const conteneur = useRef<HTMLDivElement>(null);
  const vue = useRef<EditorView | null>(null);
  const langage = useMemo(() => new Compartment(), []);
  const attributs = useMemo(() => new Compartment(), []);
  const modifiable = useMemo(() => new Compartment(), []);
  const rappels = useRef({ onChange, onEtat });
  rappels.current = { onChange, onEtat };
  const idLibelle = `editeur-${champ.nom}-libelle`;
  const idMessage = `editeur-${champ.nom}-message`;

  const attributsZone = (e: string | undefined, d: boolean) => [
    EditorView.editorAttributes.of({
      class: 'fc-editeur__zone fc-contenu',
      style: `--fc-editeur-lignes: ${champ.lignes ?? 1}`,
      ...(e ? { 'aria-invalid': 'true' } : {}),
      ...(d ? { 'aria-disabled': 'true' } : {}),
    }),
    EditorView.contentAttributes.of({
      role: 'textbox',
      'aria-multiline': 'true',
      'aria-labelledby': idLibelle,
      spellcheck: 'true',
      ...(e ? { 'aria-invalid': 'true', 'aria-describedby': idMessage } : {}),
    }),
  ];

  useEffect(() => {
    const view = new EditorView({
      parent: conteneur.current!,
      state: EditorState.create({
        doc: valeur,
        extensions: [
          extensionsCommunes,
          langage.of(langageCartes(type === 'trous')),
          attributs.of(attributsZone(erreur, desactive)),
          modifiable.of(EditorView.editable.of(!desactive)),
          placeholder(champ.exemple ?? ''),
          EditorView.updateListener.of((maj) => {
            if (maj.docChanged) rappels.current.onChange(maj.state.doc.toString());
            if (maj.docChanged || maj.selectionSet || maj.focusChanged)
              rappels.current.onEtat(maj.view);
          }),
        ],
      }),
    });
    vue.current = view;
    onVue(view);
    return () => {
      onVue(null);
      view.destroy();
      vue.current = null;
    };
    // L'éditeur est créé une fois ; les changements passent par les compartiments.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    vue.current?.dispatch({ effects: langage.reconfigure(langageCartes(type === 'trous')) });
  }, [type, langage]);

  useEffect(() => {
    vue.current?.dispatch({
      effects: [
        attributs.reconfigure(attributsZone(erreur, desactive)),
        modifiable.reconfigure(EditorView.editable.of(!desactive)),
      ],
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [erreur, desactive]);

  // Valeur changée de l'extérieur (champs vidés après l'ajout, carte rechargée).
  useEffect(() => {
    const view = vue.current;
    if (view && view.state.doc.toString() !== valeur) {
      view.dispatch({ changes: { from: 0, to: view.state.doc.length, insert: valeur } });
    }
  }, [valeur]);

  return (
    <div className="fc-editeur__champ">
      <span className="fc-editeur__libelle" id={idLibelle} onClick={() => vue.current?.focus()}>
        {champ.libelle}
      </span>
      <div ref={conteneur} className="fc-editeur__hote" />
      {erreur && (
        <MessageDeChamp id={idMessage} ton="erreur">
          {erreur}
        </MessageDeChamp>
      )}
    </div>
  );
}

export const Editeur = forwardRef<EditeurPoignee, EditeurProps>(function Editeur(
  {
    type,
    champs,
    valeur,
    onChange,
    erreurs = {},
    desactive = false,
    onEnregistrer,
    onAnnuler,
    envoyerImage,
    resoudreImage,
    couleursPersonnelles,
    onAjouterCouleur,
    onErreur,
    barreCollee = false,
    avantChamps,
  },
  ref,
) {
  const vues = useRef(new Map<NomChamp, EditorView>());
  const courant = useRef<NomChamp | null>(null);
  const [actifs, setActifs] = useState<FormatDEditeur[]>([]);
  const [surlignage, setSurlignage] = useState(lireSurlignage);
  const [surlignageCourant, setSurlignageCourant] = useState<string | null>(null);
  const rappels = useRef({ onEnregistrer, onAnnuler, onErreur, envoyerImage });
  rappels.current = { onEnregistrer, onAnnuler, onErreur, envoyerImage };
  const saisieImage = useRef<HTMLInputElement>(null);

  useImperativeHandle(ref, () => ({
    focus(nom) {
      const cible = nom ?? champs[0]?.nom;
      if (cible) vues.current.get(cible)?.focus();
    },
  }));

  function vueCourante(): EditorView | null {
    return (
      (courant.current && vues.current.get(courant.current)) ||
      vues.current.get(champs[0]!.nom) ||
      null
    );
  }

  async function insererImage(view: EditorView, fichier: File) {
    try {
      const { image, extension } = await preparerImage(fichier);
      const chemin = await rappels.current.envoyerImage(image, extension);
      view.dispatch(view.state.update(appliquerFormat(view.state, 'image', { image: { chemin } })));
      view.focus();
    } catch (e) {
      rappels.current.onErreur?.(
        e instanceof Error ? e.message : 'L’image n’a pas pu être ajoutée.',
      );
    }
  }

  function format(f: FormatDEditeur) {
    const view = vueCourante();
    if (!view) return;
    if (f === 'image') {
      saisieImage.current?.click();
      return;
    }
    view.dispatch(
      view.state.update(appliquerFormat(view.state, f), {
        scrollIntoView: true,
        userEvent: 'input',
      }),
    );
    view.focus();
  }

  function surligner(couleur: string | 'aucune') {
    const view = vueCourante();
    if (!view) return;
    if (couleur !== 'aucune') {
      setSurlignage(couleur);
      try {
        window.localStorage.setItem(CLE_SURLIGNAGE, couleur);
      } catch {
        // stockage indisponible
      }
    }
    view.dispatch(
      view.state.update(appliquerFormat(view.state, 'surlignage', { couleur }), {
        userEvent: 'input',
      }),
    );
    view.focus();
  }

  const extensionsCommunes = useMemo<Extension>(
    () => [
      history(),
      EditorView.lineWrapping,
      apercuEnDirect(resoudreImage),
      EditorView.inputHandler.of((view, from, to, texte) => {
        if (view.composing) return false;
        const spec = fermetureAuto(view.state, from, to, texte);
        if (spec === null) return false;
        view.dispatch(spec);
        return true;
      }),
      keymap.of([
        { key: 'Mod-Enter', run: () => (rappels.current.onEnregistrer?.(), true) },
        {
          key: 'Escape',
          run: () => (rappels.current.onAnnuler ? (rappels.current.onAnnuler(), true) : false),
        },
        { key: 'Enter', run: entree },
        { key: 'Backspace', run: supprimerPaireVide },
        {
          key: 'Tab',
          run: (v) => (dansListeOuCode(v.state) ? indentMore(v) : false),
          shift: (v) => (dansListeOuCode(v.state) ? indentLess(v) : false),
        },
        {
          key: 'Mod-b',
          run: (v) => (v.dispatch(v.state.update(appliquerFormat(v.state, 'gras'))), true),
        },
        {
          key: 'Mod-i',
          run: (v) => (v.dispatch(v.state.update(appliquerFormat(v.state, 'italique'))), true),
        },
        {
          key: 'Mod-k',
          run: (v) => (v.dispatch(v.state.update(appliquerFormat(v.state, 'lien'))), true),
        },
        {
          key: 'Mod-Shift-c',
          run: (v) =>
            type === 'trous'
              ? (v.dispatch(v.state.update(appliquerFormat(v.state, 'trou'))), true)
              : false,
        },
        ...markdownKeymap.filter((b) => b.key !== 'Enter'),
        ...historyKeymap,
        ...defaultKeymap,
      ]),
      EditorView.domEventHandlers({
        paste(evenement, view) {
          const fichier = [...(evenement.clipboardData?.files ?? [])].find((f) =>
            f.type.startsWith('image/'),
          );
          if (!fichier) return false;
          evenement.preventDefault();
          void insererImage(view, fichier);
          return true;
        },
        drop(evenement, view) {
          const fichier = [...(evenement.dataTransfer?.files ?? [])].find((f) =>
            f.type.startsWith('image/'),
          );
          if (!fichier) return false;
          evenement.preventDefault();
          const pos = view.posAtCoords({ x: evenement.clientX, y: evenement.clientY });
          if (pos !== null) view.dispatch({ selection: { anchor: pos } });
          void insererImage(view, fichier);
          return true;
        },
      }),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [type, resoudreImage],
  );

  return (
    <>
      {type !== 'image' && type !== 'tableau' && (
        <BarreDOutilsEditeur
          type={type}
          formatsActifs={actifs}
          surlignage={surlignage}
          surlignageCourant={surlignageCourant}
          couleursPersonnelles={couleursPersonnelles}
          onFormat={format}
          onSurligner={surligner}
          onAjouterCouleur={(couleur) => {
            onAjouterCouleur(couleur);
            surligner(couleur);
          }}
          collee={barreCollee}
          desactivee={desactive}
        />
      )}
      {avantChamps}
      <div className="fc-editeur">
        {champs.map((champ) => (
          <Champ
            key={`${type}-${champ.nom}`}
            champ={champ}
            type={type}
            valeur={valeur[champ.nom] ?? ''}
            erreur={erreurs[champ.nom]}
            desactive={desactive}
            extensionsCommunes={extensionsCommunes}
            onChange={(markdown) => onChange(champ.nom, markdown)}
            onVue={(view) => {
              if (view) vues.current.set(champ.nom, view);
              else vues.current.delete(champ.nom);
            }}
            onEtat={(view) => {
              if (view.hasFocus) courant.current = champ.nom;
              if (courant.current === champ.nom) {
                setActifs(formatsActifs(view.state));
                setSurlignageCourant(surlignageAuCurseur(view.state));
              }
            }}
          />
        ))}
      </div>
      <input
        ref={saisieImage}
        type="file"
        accept="image/*"
        className="fc-sr"
        tabIndex={-1}
        aria-hidden="true"
        onChange={(e) => {
          const fichier = e.target.files?.[0];
          const view = vueCourante();
          if (fichier && view) void insererImage(view, fichier);
          e.target.value = '';
        }}
      />
    </>
  );
});
